import { useState, useMemo, useEffect } from "react";
import { sortRows, filterRows, paginateRows } from "./helpers";
import { Pagination } from "./Table.Pagination";
import { Table as BootstrapTable } from "react-bootstrap";
import TableFilterForm from "./Table.FilterForm";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import "./Table.styles.css";
import { useWindowSize } from "common/hooks/useWindowSize";

const ROW_HEIGHT = 47.4;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const sortInitialValue = { order: "asc", orderBy: "date" };
const filtersInitialValue = {};

const Table = ({
  config: { name = "table", columns = [] },
  rows = [],
  actions = [],
  loading = false,
  error = "",
  hasSapConfirmButtons = false,
  hasSapDownloadButtons = false,
  hasSapGenerateButton = false,
  internalOrderFields = [],
  highlightedRowColors = {
    background: "hsl(0, 0%, 95%)",
    color: "hsl(0, 0%, 60%)",
  },
  highlightedRows = [],
  paginated = false,
  filtered = false,
  ...props
}) => {
  const { width } = useWindowSize();
  const [activePage, setActivePage] = useState(1);
  const [filters, setFilters] = useState(filtersInitialValue);
  const [sort, setSort] = useState(sortInitialValue);
  const [rowsPerPage, setRowsPerPage] = useState(paginated ? 5 : 1);

  const accessors = useMemo(
    () => columns.map((column) => column.accessor),
    [columns]
  );
  const filteredRows = useMemo(
    () => filterRows(rows, filters),
    [rows, filters]
  );
  const sortedRows = useMemo(
    () => (rows ? sortRows(filteredRows, sort) : []),
    [filteredRows, sort]
  );
  const calculatedRows = paginated
    ? paginateRows(sortedRows, activePage, rowsPerPage)
    : rows;
  const rowsPerPageSelectOptions = useMemo(
    () =>
      ROWS_PER_PAGE_OPTIONS.filter(
        (option) => option <= rows.length || option === 5
      ),
    [rows]
  );
  const rowHeight = useMemo(() => {
    // ! If table is not rendered then offsetHeight is 0. Check which table row is selected.
    const rowElement = document.getElementById(`${name}-table-row-0`);
    if (rowElement?.offsetHeight) return rowElement?.offsetHeight;
    else return ROW_HEIGHT;
  }, [width]);

  const totalPages = useMemo(
    () => Math.ceil(filteredRows.length / rowsPerPage),
    [filteredRows, rowsPerPage]
  );
  const emptyRowHeight = useMemo(() => {
    let rowsOnPage = 0;
    if (loading) {
      rowsOnPage = rowsPerPage;
    } else {
      rowsOnPage = calculatedRows.length ? calculatedRows.length : 1;
    }
    if (rowsPerPage - rowsOnPage === 0) return;
    return (rowsPerPage - rowsOnPage) * (rowHeight + 1) - 1;
  }, [rowsPerPage, rowHeight, calculatedRows]);

  const handleSearch = (value, accessors) => {
    setActivePage(1);
    let newFilters = {};
    accessors?.forEach((accessor) => {
      newFilters[accessor] = value;
    });

    if (value) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...newFilters,
      }));
    } else {
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };

        accessors?.forEach((accessor) => {
          delete updatedFilters[accessor];
        });

        return updatedFilters;
      });
    }
  };

  const handleSort = (accessor) => {
    setActivePage(1);
    setSort((prevSort) => ({
      order:
        prevSort?.order === "asc" && prevSort?.orderBy === accessor
          ? "desc"
          : "asc",
      orderBy: accessor,
    }));
  };

  const clearAll = () => {
    setSort(sortInitialValue);
    setFilters(filtersInitialValue);
  };

  // !TODO: check row height when there are less records than chosen from dropdown

  useEffect(() => {
    // On tab switch set active page to one
    if (loading) setActivePage(1);
  }, [loading]);

  return (
    <>
      {filtered ? (
        <TableFilterForm
          handleSearch={handleSearch}
          loading={loading}
          accessors={accessors}
          clearAll={clearAll}
        />
      ) : null}
      <div className="tea2-table__border">
        <BootstrapTable
          responsive
          className="tea2-table"
          id={name + "-table-body"}
          {...props}
        >
          <TableHeader columns={columns} sort={sort} handleSort={handleSort} />
          <TableBody
            tableName={name}
            columns={columns}
            rows={calculatedRows}
            emptyRowHeight={emptyRowHeight}
            rowHeight={ROW_HEIGHT}
            rowsPerPage={rowsPerPage}
            loading={loading}
            error={error}
            actions={actions}
            internalOrderFields={internalOrderFields}
            hasSapDownloadButtons={hasSapDownloadButtons}
            hasSapConfirmButtons={hasSapConfirmButtons}
            highlightedRows={highlightedRows}
            highlightedRowColors={highlightedRowColors}
          />
        </BootstrapTable>
      </div>
      {paginated ? (
        <Pagination
          id={name + "-pagination"}
          activePage={activePage}
          rowsCount={sortedRows?.length}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
          setActivePage={setActivePage}
          loading={loading}
          rowsPerPageSelectOptions={rowsPerPageSelectOptions}
        />
      ) : null}
    </>
  );
};

export default Table;
